import { apiGet } from '../services/api';
import { dashboardPathForRole } from '../context/AuthContext';

export const COMPANY_ONBOARDING_PATH = '/company/onboarding';
export const COMPANY_DASHBOARD_PATH = '/company/dashboard';

/**
 * Resolves post-login destination for company users based on onboarding status.
 */
export async function resolveCompanyDestination(apiBase, token, role) {
  if (role !== 'company') {
    return dashboardPathForRole(role);
  }
  if (!apiBase || !token) {
    return COMPANY_ONBOARDING_PATH;
  }
  try {
    const res = await apiGet('/api/v1/company/onboarding/status', token);
    if (res.status === 401) {
      return '/login';
    }
    if (!res.ok) {
      return COMPANY_ONBOARDING_PATH;
    }
    const data = await res.json();
    if (data?.data?.has_company) {
      return COMPANY_DASHBOARD_PATH;
    }
    return COMPANY_ONBOARDING_PATH;
  } catch {
    return COMPANY_ONBOARDING_PATH;
  }
}

/**
 * @returns {Promise<{ has_company: boolean, company: object|null }|null>}
 */
export async function fetchCompanyOnboardingStatus(token) {
  // Module-level promise cache to avoid duplicate requests when multiple
  // components need the onboarding status independently.
  if (
    typeof fetchCompanyOnboardingStatus._cachedPromise !== 'undefined' &&
    fetchCompanyOnboardingStatus._cachedToken === token
  ) {
    return fetchCompanyOnboardingStatus._cachedPromise;
  }

  const promise = (async () => {
    const res = await apiGet('/api/v1/company/onboarding/status', token);
    if (!res.ok) {
      return null;
    }
    const data = await res.json();
    return data?.data ?? null;
  })();

  fetchCompanyOnboardingStatus._cachedToken = token;
  fetchCompanyOnboardingStatus._cachedPromise = promise;

  return promise;
}

/**
 * Invalidate the module-level onboarding status cache (e.g. after create).
 */
export function clearCompanyOnboardingStatusCache() {
  delete fetchCompanyOnboardingStatus._cachedPromise;
  delete fetchCompanyOnboardingStatus._cachedToken;
}

// Internal fields for module-level caching (not part of the public API).
fetchCompanyOnboardingStatus._cachedToken = null;
fetchCompanyOnboardingStatus._cachedPromise = undefined;
